import { useCallback, useEffect, useState } from "react";
import {
  COPILOT_WELCOME,
  COPILOT_STORAGE_KEY,
  createThreadId,
  deriveThreadTitle,
} from "./copilotUtils";

const WELCOME_MESSAGE = {
  role: "assistant",
  content: COPILOT_WELCOME,
  toolsRan: [],
  suggestedActions: [],
  toolResults: [],
  blocks: [],
  draftEmailText: null,
  workflow: null,
  listSession: null,
  pendingEntityQuery: null,
  lastListFilters: null,
  lastMetricCode: null,
  pendingConfirmation: null,
};

const loadThreads = () => {
  try {
    const raw = sessionStorage.getItem(COPILOT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((thread) => {
      const only = thread?.messages;
      if (
        Array.isArray(only) &&
        only.length === 1 &&
        only[0]?.role === "assistant" &&
        String(only[0].content || "").includes("**Candidates**") &&
        String(only[0].content || "").includes("**Attention and reports**")
      ) {
        return {
          ...thread,
          messages: [{ ...only[0], content: COPILOT_WELCOME }],
        };
      }
      return thread;
    });
  } catch {
    return [];
  }
};

const persistThreads = (threads) => {
  try {
    sessionStorage.setItem(COPILOT_STORAGE_KEY, JSON.stringify(threads.slice(0, 10)));
  } catch {
    /* ignore quota errors */
  }
};

const buildWelcomeThread = (pageContext) => {
  const id = createThreadId();
  return {
    id,
    title: "New chat",
    module: pageContext?.module || "Recruitment",
    pageContext: { ...(pageContext || {}) },
    conversationId: id,
    workflow: null,
    listSession: null,
    pendingEntityQuery: null,
    lastListFilters: null,
    lastMetricCode: null,
    attachments: [],
    messages: [WELCOME_MESSAGE],
    updatedAt: Date.now(),
  };
};

const getNeighborThreadId = ({ deletedThreadId, previousThreads, nextThreads }) => {
  const deletedIndex = previousThreads.findIndex((thread) => thread.id === deletedThreadId);
  const neighbor = nextThreads[Math.min(deletedIndex, nextThreads.length - 1)];
  return neighbor?.id || nextThreads[0]?.id || null;
};

export const useCopilotThreads = (pageContext) => {
  const [threads, setThreads] = useState(() => loadThreads());
  const [activeThreadId, setActiveThreadId] = useState(() => loadThreads()[0]?.id || null);

  useEffect(() => {
    persistThreads(threads);
  }, [threads]);

  const activeThread =
    threads.find((t) => t.id === activeThreadId) ||
    threads[0] ||
    null;

  const updateThread = useCallback((threadId, patch) => {
    setThreads((prev) => prev.map((t) => (t.id === threadId ? { ...t, ...patch } : t)));
  }, []);

  const createThread = useCallback(() => {
    const thread = buildWelcomeThread(pageContext);
    setThreads((prev) => [thread, ...prev.filter((t) => t.id !== thread.id)].slice(0, 10));
    setActiveThreadId(thread.id);
    return thread;
  }, [pageContext]);

  const ensureActiveThread = useCallback(() => {
    if (activeThread) return activeThread;
    return createThread();
  }, [activeThread, createThread]);

  const deleteThread = useCallback(
    (threadId) => {
      setThreads((prev) => {
        const next = prev.filter((t) => t.id !== threadId);
        if (next.length === 0) {
          const fresh = buildWelcomeThread(pageContext);
          setActiveThreadId(fresh.id);
          return [fresh];
        }
        if (activeThreadId === threadId) {
          setActiveThreadId(
            getNeighborThreadId({
              deletedThreadId: threadId,
              previousThreads: prev,
              nextThreads: next,
            })
          );
        }
        return next;
      });
    },
    [activeThreadId, pageContext]
  );

  const clearAllThreads = useCallback(() => {
    try {
      sessionStorage.removeItem(COPILOT_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    const fresh = buildWelcomeThread(pageContext);
    setThreads([fresh]);
    setActiveThreadId(fresh.id);
    return fresh;
  }, [pageContext]);

  const setThreadMessages = useCallback(
    (threadId, messages) => updateThread(threadId, { messages, updatedAt: Date.now() }),
    [updateThread]
  );

  const setThreadWorkflow = useCallback(
    (threadId, workflow) => updateThread(threadId, { workflow }),
    [updateThread]
  );

  const setThreadEntityEcho = useCallback(
    (threadId, { listSession, pendingEntityQuery, lastListFilters, lastMetricCode }) =>
      updateThread(threadId, {
        listSession: listSession ?? null,
        pendingEntityQuery: pendingEntityQuery ?? null,
        lastListFilters: lastListFilters ?? null,
        lastMetricCode: lastMetricCode ?? null,
      }),
    [updateThread]
  );

  const setThreadConversationId = useCallback(
    (threadId, conversationId) => updateThread(threadId, { conversationId }),
    [updateThread]
  );

  const setThreadAttachments = useCallback(
    (threadId, attachments) => updateThread(threadId, { attachments }),
    [updateThread]
  );

  const maybeSetThreadTitle = useCallback((threadId, firstUserMessage) => {
    setThreads((prev) =>
      prev.map((t) => {
        if (t.id !== threadId || (t.title && t.title !== "New chat")) return t;
        return {
          ...t,
          title: deriveThreadTitle(firstUserMessage, t.pageContext),
        };
      })
    );
  }, []);

  return {
    threads,
    activeThread,
    activeThreadId,
    setActiveThreadId,
    createThread,
    deleteThread,
    clearAllThreads,
    ensureActiveThread,
    updateThread,
    setThreadMessages,
    setThreadWorkflow,
    setThreadEntityEcho,
    setThreadConversationId,
    setThreadAttachments,
    maybeSetThreadTitle,
  };
};

export { WELCOME_MESSAGE };
